import { z } from 'zod';
import { HstTreatmentSchema, ShiftSettingSchema, ShiftTimeBucketSchema, } from '../rate/enums.schema.js';
import { MatchingStateSchema, ShiftStatusSchema } from './enums.schema.js';
import { BuyerVisibleCaregiverProfileSchema } from './buyer-caregiver-profile.schema.js';
import { ShiftListQuerySchema } from './list-scope.schema.js';
// Phase 9e — buyer portal response shapes.
//
// Buyer = facility user OR homecare client. The shapes converge on a
// shared response type with persona-specific list filtering done at
// the service layer (facility scopes by facilityId; homecare client
// scopes by homecareClientId + recipientId).
export const BuyerShiftRequestListItemSchema = z.object({
    id: z.string().uuid(),
    roleCode: z.string(),
    setting: ShiftSettingSchema,
    scheduledStart: z.string().datetime(),
    scheduledEnd: z.string().datetime(),
    shiftTimeBucket: ShiftTimeBucketSchema,
    matchingState: MatchingStateSchema,
    matchingStateLabel: z.string(), // human-friendly: "Searching", "Confirmed", etc.
    recipientId: z.string().uuid().nullable(), // homecare only
    recipientName: z.string().nullable(), // homecare display
    specialConstraints: z.string().nullable(),
    requiredSkills: z.array(z.string()),
    expiresAt: z.string().datetime(),
    cancelledAt: z.string().datetime().nullable(),
    // If matching produced a shift, embed the assigned caregiver profile.
    assignedCaregiver: BuyerVisibleCaregiverProfileSchema.nullable(),
    createdAt: z.string().datetime(),
});
export const BuyerShiftRequestListResponseSchema = z.object({
    items: z.array(BuyerShiftRequestListItemSchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
export const BuyerShiftRequestListQuerySchema = z.object({
    // `active`  — live requests being matched now (MATCHING / REBROADCASTING_T2 /
    //             PENDING_TARGETED). The default the "Current requests" tab shows.
    // `history` — 3.4: active PLUS the terminal-but-unfilled states (EXPIRED_* /
    //             CANCELLED_*), so a facility can see what happened to a posting
    //             that fell off the list. EXCLUDES FILLED (those are Booked shifts —
    //             showing them here would double-count the same posting) and
    //             EXCLUDES SCHEDULED (future/queued, not history).
    // `past`/`all` retained for completeness (they include FILLED); the portal
    //             uses active↔history via the "Show expired & cancelled" toggle.
    status: z
        .enum(['active', 'history', 'past', 'all'])
        .default('active'),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
});
// Caregiver-category options for the Post-a-shift picker. Scoped to the
// buyer's setting at the service layer (facility → LTC roles; homecare →
// HOMECARE roles), so the form never exposes a raw Role UUID.
export const RoleOptionSchema = z.object({
    id: z.string().uuid(),
    code: z.string(), // PSW / RPN / RN / COMPANION
    name: z.string(), // "Personal Support Worker", etc.
});
export const RoleOptionListResponseSchema = z.object({
    roles: z.array(RoleOptionSchema),
});
// Phase 21 (3.1-8) — caregivers a facility has actually worked with (had a
// non-cancelled shift assigned), for the preferred-caregiver picker. No
// browse-all directory; a facility can only prefer someone they've had before.
export const WorkedWithCaregiverSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    roleCodes: z.array(z.string()),
});
export const WorkedWithCaregiverListResponseSchema = z.object({
    caregivers: z.array(WorkedWithCaregiverSchema),
});
export const BuyerShiftListItemSchema = z.object({
    id: z.string().uuid(),
    shiftRequestId: z.string().uuid(),
    status: ShiftStatusSchema,
    setting: ShiftSettingSchema,
    roleCode: z.string(),
    scheduledStart: z.string().datetime(),
    scheduledEnd: z.string().datetime(),
    // MARGIN BOUNDARY — a buyer sees ONLY what they are billed.
    //
    // `payRateAtBooking` (what TrueCare pays the care professional) was being
    // shipped here too, so any facility or family could compute the platform's
    // exact spread (bill − pay). It was never even rendered — a pure leak.
    // The spread is the platform's margin (Q2 managed-marketplace model); it is
    // not a buyer-visible field. Do NOT re-add payRateAtBooking to this shape.
    //
    // NULLABLE for facility RBAC 3.2: a facility login without the RATE_VIEW
    // capability (e.g. a pure Coordinator) may read a shift but NOT its pricing,
    // so the facility shift service blanks this to null for them. Homecare (a
    // single-user buyer with no roles) always sends the real value; the frontend
    // renders null as "—".
    billRateAtBooking: z.string().nullable(),
    hstTreatmentAtBooking: HstTreatmentSchema,
    shiftTimeBucketAtBooking: ShiftTimeBucketSchema,
    recipientId: z.string().uuid().nullable(),
    recipientName: z.string().nullable(),
    acceptedViaManualAssign: z.boolean(),
    actualStart: z.string().datetime().nullable(),
    actualEnd: z.string().datetime().nullable(),
    breakMinutes: z.number().int().nullable(),
    hoursActual: z.string().nullable(),
    completedAt: z.string().datetime().nullable(),
    cancelledAt: z.string().datetime().nullable(),
    cancelActorPersona: z.string().nullable(),
    cancelReason: z.string().nullable(),
    hasRating: z.boolean(),
    assignedCaregiver: BuyerVisibleCaregiverProfileSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const BuyerShiftListResponseSchema = z.object({
    items: z.array(BuyerShiftListItemSchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
/** Doc 17 — the shared shift-list query (scope + optional status + paging). */
export const BuyerShiftListQuerySchema = ShiftListQuerySchema;
export const SignTimesheetRequestSchema = z
    .object({
    actualStart: z.string().datetime(),
    actualEnd: z.string().datetime(),
    breakMinutes: z.number().int().min(0).max(720),
    completionNotes: z.string().max(2000).optional(),
})
    .refine((v) => new Date(v.actualEnd).getTime() > new Date(v.actualStart).getTime(), {
    message: 'actualEnd must be strictly after actualStart',
    path: ['actualEnd'],
});
export const BuyerShiftActionWithReasonSchema = z.object({
    reason: z.string().min(10).max(500),
});
/**
 * Change-list §5.2 (2026-07-22) — operator targets one OR MORE hand-picked
 * caregivers for a pending request (a shortlist push). Each gets a tier-1
 * targeted offer; first-accept-wins among them (the accept advisory lock is the
 * backstop). `caregiverIds` is a set (deduped server-side), capped at 10 to
 * bound the fan-out. The single-target case is `caregiverIds: [id]`.
 */
export const OperatorTargetCaregiverRequestSchema = z.object({
    caregiverIds: z.array(z.string().uuid()).min(1).max(10),
    reason: z.string().min(20).max(500),
});
/**
 * Phase 10f audit follow-up — operator-initiated shift cancellation.
 *
 * Wires the existing ShiftLifecycleService.cancelByOperator method
 * (shipped 9c-5 but previously unexposed) as an admin endpoint so
 * Workflows H/I/K (operator corrects a DRAFT/ISSUED invoice over a
 * facility dispute by cancelling the underlying shift) work end-to-end.
 *
 * `requeueForMatching=true` triggers a rematch on the underlying
 * shift_request; `false` leaves it CANCELLED_BY_OPERATOR (the corrected-
 * invoice path almost always uses `false` — the operator doesn't want
 * the disputed shift re-broadcast).
 */
export const OperatorCancelShiftRequestSchema = z.object({
    reason: z.string().min(10).max(500),
    requeueForMatching: z.boolean().default(false),
});
/**
 * Phase 21 (3.1-6) — operator void-and-reassign.
 *
 * Voids a booked shift and re-posts the request. `preferredCaregiverId`
 * omitted / null → re-post to the open pool; a UUID → that caregiver gets
 * first refusal (priority offer + 24h fallback capped at shift start) before it
 * opens to everyone. Distinct from cancel-by-operator: this always requeues and
 * routes through the preferred-aware match path.
 */
export const OperatorReassignShiftRequestSchema = z.object({
    reason: z.string().min(10).max(500),
    preferredCaregiverId: z.string().uuid().nullish(),
});
//# sourceMappingURL=buyer-views.schema.js.map